export type VoiceControllerState =
  | 'idle'
  | 'recording'
  | 'captured'
  | 'permission-denied'
  | 'error';

export type VoiceControllerErrorType =
  | 'permission-denied'
  | 'no-microphone'
  | 'unsupported'
  | 'recognition';

export interface VoiceControllerTranscriptDetail {
  finalTranscript: string;
  interimTranscript: string;
  transcript: string;
}

export interface VoiceControllerStateDetail {
  state: VoiceControllerState;
}

export interface VoiceControllerErrorDetail {
  type: VoiceControllerErrorType;
  originalError: unknown;
  code?: string;
  message: string;
}

export interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

export interface SpeechRecognitionResultLike {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

export interface SpeechRecognitionResultListLike {
  length: number;
  [index: number]: SpeechRecognitionResultLike;
}

export interface SpeechRecognitionResultEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

export interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}

export interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: null | (() => void);
  onerror: null | ((event: SpeechRecognitionErrorEventLike) => void);
  onresult: null | ((event: SpeechRecognitionResultEventLike) => void);
  start(): void;
  stop(): void;
  abort(): void;
}

export interface PermissionsLike {
  query(descriptor: { name: string }): Promise<{ state: string }>;
}

export interface SpeechRecognitionControllerOptions {
  lang?: string;
  permissions?: PermissionsLike | null;
  speechRecognitionFactory?: (() => SpeechRecognitionLike) | null;
}

const PERMISSION_ERROR_CODES = new Set(['not-allowed', 'service-not-allowed']);

const appendSegment = (currentValue: string, nextValue: string): string => {
  const normalizedValue = nextValue.trim();

  if (!normalizedValue) {
    return currentValue;
  }

  if (!currentValue) {
    return normalizedValue;
  }

  return `${currentValue} ${normalizedValue}`;
};

const mapRecognitionErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission was denied. Review the site permission settings and try again.';
    case 'audio-capture':
      return 'No microphone is available on this device.';
    case 'no-speech':
      return 'No speech was detected.';
    case 'network':
      return 'A network error interrupted speech recognition.';
    case 'aborted':
      return 'Speech recognition was aborted.';
    default:
      return 'Speech recognition failed.';
  }
};

export class SpeechRecognitionController extends EventTarget {
  private readonly permissions: PermissionsLike | null;
  private readonly speechRecognitionFactory: (() => SpeechRecognitionLike) | null;
  private recognition: SpeechRecognitionLike | null = null;
  private finalTranscript = '';
  private interimTranscript = '';
  private state: VoiceControllerState = 'idle';
  private lang: string;
  private isUserStopping = false;

  constructor(options: SpeechRecognitionControllerOptions = {}) {
    super();

    this.lang = options.lang ?? 'es-MX';
    this.permissions = options.permissions ?? this.getDefaultPermissions();
    this.speechRecognitionFactory =
      options.speechRecognitionFactory === undefined
        ? this.getDefaultSpeechRecognitionFactory()
        : options.speechRecognitionFactory;
  }

  get currentState(): VoiceControllerState {
    return this.state;
  }

  get transcript(): string {
    return appendSegment(this.finalTranscript, this.interimTranscript);
  }

  get finalText(): string {
    return this.finalTranscript;
  }

  get interimText(): string {
    return this.interimTranscript;
  }

  setLanguage(nextLanguage: string): void {
    this.lang = nextLanguage;

    if (this.recognition) {
      this.recognition.lang = nextLanguage;
    }
  }

  async start(): Promise<void> {
    if (!this.speechRecognitionFactory) {
      this.handleUnsupported();
      return;
    }

    const permissionState = await this.getMicrophonePermissionState();

    if (permissionState === 'denied') {
      this.handlePermissionDenied({
        error: 'not-allowed',
        message: 'The browser reported a denied microphone permission.',
      });
      return;
    }

    this.ensureRecognition();
    this.resetTranscripts();
    this.isUserStopping = false;
    this.setState('recording');
    this.emitTranscript();

    try {
      this.recognition?.start();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Speech recognition could not start.';
      this.setState('error');
      this.emitError({
        type: 'recognition',
        originalError: error,
        message,
      });
    }
  }

  stop(): void {
    if (this.state !== 'recording') {
      return;
    }

    this.isUserStopping = true;
    this.recognition?.stop();
  }

  abort(): void {
    this.isUserStopping = true;
    this.recognition?.abort();
  }

  private handleUnsupported(): void {
    this.setState('error');
    this.emitError({
      type: 'unsupported',
      originalError: new Error('window.webkitSpeechRecognition is not available.'),
      message: 'This browser does not support the Web Speech API.',
    });
  }

  private getDefaultPermissions(): PermissionsLike | null {
    if (typeof navigator === 'undefined' || !navigator.permissions) {
      return null;
    }

    return navigator.permissions as unknown as PermissionsLike;
  }

  private getDefaultSpeechRecognitionFactory(): (() => SpeechRecognitionLike) | null {
    const target = globalThis as typeof globalThis & {
      webkitSpeechRecognition?: new () => SpeechRecognitionLike;
    };

    if (!target.webkitSpeechRecognition) {
      return null;
    }

    return () => new target.webkitSpeechRecognition!();
  }

  private async getMicrophonePermissionState(): Promise<string | null> {
    if (!this.permissions) {
      return null;
    }

    try {
      const permissionStatus = await this.permissions.query({
        name: 'microphone' as PermissionName,
      });

      return permissionStatus.state;
    } catch {
      return null;
    }
  }

  private ensureRecognition(): void {
    if (this.recognition || !this.speechRecognitionFactory) {
      return;
    }

    this.recognition = this.speechRecognitionFactory();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = this.lang;
    this.recognition.onresult = (event) => this.handleResult(event);
    this.recognition.onerror = (event) => this.handleErrorEvent(event);
    this.recognition.onend = () => this.handleEnd();
  }

  private handleResult(event: SpeechRecognitionResultEventLike): void {
    let nextInterimTranscript = '';

    for (let index = event.resultIndex; index < event.results.length; index += 1) {
      const result = event.results[index];
      const segment = result[0]?.transcript ?? '';

      if (result.isFinal) {
        this.finalTranscript = appendSegment(this.finalTranscript, segment);
      } else {
        nextInterimTranscript = appendSegment(nextInterimTranscript, segment);
      }
    }

    this.interimTranscript = nextInterimTranscript;
    this.emitTranscript();
  }

  private handleErrorEvent(event: SpeechRecognitionErrorEventLike): void {
    const { error } = event;

    if (PERMISSION_ERROR_CODES.has(error)) {
      this.handlePermissionDenied(event);
      return;
    }

    if (error === 'audio-capture') {
      this.setState('error');
      this.emitError({
        type: 'no-microphone',
        originalError: event,
        code: error,
        message: mapRecognitionErrorMessage(error),
      });
      return;
    }

    if (error === 'no-speech') {
      this.emitError({
        type: 'recognition',
        originalError: event,
        code: error,
        message: mapRecognitionErrorMessage(error),
      });
      return;
    }

    if (error === 'aborted') {
      if (!this.isUserStopping) {
        this.emitError({
          type: 'recognition',
          originalError: event,
          code: error,
          message: mapRecognitionErrorMessage(error),
        });
      }

      return;
    }

    if (error === 'network') {
      this.emitError({
        type: 'recognition',
        originalError: event,
        code: error,
        message: mapRecognitionErrorMessage(error),
      });
      return;
    }

    this.setState('error');
    this.emitError({
      type: 'recognition',
      originalError: event,
      code: error,
      message: mapRecognitionErrorMessage(error),
    });
  }

  private handlePermissionDenied(event: SpeechRecognitionErrorEventLike): void {
    this.isUserStopping = true;
    this.setState('permission-denied');
    this.emitError({
      type: 'permission-denied',
      originalError: event,
      code: event.error,
      message: mapRecognitionErrorMessage(event.error),
    });
  }

  private handleEnd(): void {
    if (this.state === 'permission-denied' || this.state === 'error') {
      this.isUserStopping = false;
      return;
    }

    if (this.isUserStopping) {
      this.isUserStopping = false;
      this.interimTranscript = '';
      this.setState(this.finalTranscript ? 'captured' : 'idle');
      this.emitTranscript();
      return;
    }

    if (this.state !== 'recording') {
      return;
    }

    try {
      this.recognition?.start();
    } catch (error) {
      this.setState('error');
      this.emitError({
        type: 'recognition',
        originalError: error,
        message: error instanceof Error ? error.message : 'Speech recognition could not restart.',
      });
    }
  }

  private resetTranscripts(): void {
    this.finalTranscript = '';
    this.interimTranscript = '';
  }

  private setState(nextState: VoiceControllerState): void {
    if (this.state === nextState) {
      return;
    }

    this.state = nextState;
    this.dispatchEvent(
      new CustomEvent<VoiceControllerStateDetail>('statechange', {
        detail: { state: nextState },
      }),
    );
  }

  private emitTranscript(): void {
    this.dispatchEvent(
      new CustomEvent<VoiceControllerTranscriptDetail>('transcript', {
        detail: {
          finalTranscript: this.finalTranscript,
          interimTranscript: this.interimTranscript,
          transcript: this.transcript,
        },
      }),
    );
  }

  private emitError(detail: VoiceControllerErrorDetail): void {
    this.dispatchEvent(
      new CustomEvent<VoiceControllerErrorDetail>('error', {
        detail,
      }),
    );
  }
}

